import React, { PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import UIButton from 'Components/UIComponents/UIButton';
import dayjs from 'dayjs';
import { PlanDetails, PlanTypes } from 'Interfaces/Billing';
import { DateFormats, User } from 'Interfaces/User';
import { selectSubscriptionInfo, selectUser, selectUserPlan } from 'Utils/selectors';

interface BillingCancelPlanWizardLoseSubscriptionProps {
  onCancel?: () => void;
  isSubmitting?: boolean;
  hasValidationErrors?: boolean;
}

const BillingCancelPlanWizardLoseSubscription = ({
  children,
  isSubmitting,
  hasValidationErrors,
  onCancel,
}: PropsWithChildren<BillingCancelPlanWizardLoseSubscriptionProps>) => {
  const userPlan: PlanDetails = useSelector(selectUserPlan);
  const user: User = useSelector(selectUser);
  const subscriptionInfo = useSelector(selectSubscriptionInfo);

  const isTrial = user.isTrialSubscription;
  const isPersonalPlan = userPlan?.type === PlanTypes.PERSONAL;
  const isBusinessPlan = userPlan?.type === PlanTypes.BUSINESS;

  const subscriptionEndDate = useMemo(() => {
    return subscriptionInfo && subscriptionInfo.nextBillingDate
      ? dayjs(subscriptionInfo.nextBillingDate).format(DateFormats.MM_DD_YYYY)
      : undefined;
  }, [subscriptionInfo]);

  return (
    <>
      <h1 className="billing__plan-cancel-modal-title">Sorry to see you go</h1>
      <p className="billing__plan-cancel-modal-paragraph">
        {`If you cancel your ${isTrial ? 'trial' : 'subscription'}, you will ${
          isTrial ? 'immediately lose' : 'keep'
        } access to the ${
          isPersonalPlan ? 'Personal Plan' : isBusinessPlan ? 'Business Plan' : ''
        } features such as ${
          isBusinessPlan
            ? 'Unlimited Templates, Unlimited Signature Requests, Signaturely Forms, Bulk Send, Custom Branding'
            : '1 Template, 5 Signature Requests'
        } and ${subscriptionEndDate ? 'more on ' + subscriptionEndDate + '.' : 'more.'}`}
      </p>
      {children}
      <div className="billing__plan-cancel-modal-actions">
        <UIButton
          priority="primary"
          title="Nevermind"
          className="billing__plan-cancel-modal-button--primary"
          handleClick={onCancel}
        />
        <UIButton
          priority="white"
          title="Cancel Subscription"
          className="billing__plan-cancel-modal-button--white"
          type="submit"
          disabled={hasValidationErrors}
          isLoading={isSubmitting}
        />
      </div>
    </>
  );
};

export default BillingCancelPlanWizardLoseSubscription;
